import { useState, useCallback, useEffect, useRef } from 'react'
import req from '@sportninja/common/api/request'
import { schedulerUnavailableMessage } from '../schedulerUnavailable'
import { extractSchedulerErrorCode } from '../utils/schedulerError'
// Types from useSchedulerFlights used for reference

/**
 * Convert a thrown request error into an Error with a user-facing
 * message. Structured backend errors (pre_fix_flight, unknown_slot_uid)
 * carry `error` + `message` on the response body; keep those fields on
 * the returned Error so UI layers can discriminate.
 *
 * The generic fall-through must PRESERVE the backend's message and its
 * structured code. `req()` throws a 4xx body as a plain object, so collapsing
 * it into `new Error('Operation failed')` destroyed both — which is how a fresh
 * UNKNOWN_SLOT_DIVISION from /retry reached the page as an unrecognizable
 * "Operation failed", lost to a stale SOLVER_TIMEOUT still sitting on the
 * flight, and routed the admin to a "Retry" that could only fail again.
 *
 * Exported for direct unit testing (a pure call sidesteps the @emotion/react
 * render blocker that prevents rendering this hook's components in Jest).
 */
export const toSchedulerError = (
  err: unknown
): Error & {
  error?: string
  error_code?: string
  action?: string
  status?: number
} => {
  if (err && typeof err === 'object' && 'error' in err) {
    const apiError = err as {
      error?: string
      message?: string
      action?: string
      status?: number
    }
    if (apiError.error === 'pre_fix_flight') {
      const e = new Error(
        apiError.message ??
          'This flight was generated before a scheduling bug fix and cannot be partially retried. Please regenerate from scratch.'
      ) as Error & { error?: string; action?: string; status?: number }
      e.error = 'pre_fix_flight'
      e.action = 'regenerate'
      e.status = apiError.status
      return e
    }
    if (apiError.error === 'unknown_slot_uid') {
      const e = new Error(
        apiError.message ??
          'Your time slot list is stale — refresh the page and try again.'
      ) as Error & { error?: string; action?: string; status?: number }
      e.error = 'unknown_slot_uid'
      e.action = 'refresh'
      e.status = apiError.status
      return e
    }
  }
  // The friendly scheduler-unavailable 503 (api2's SchedulerDrainingException)
  // arrives via req's `status > 500` path, nesting the typed body under
  // `err.bodyData` — see schedulerUnavailableMessage (shared with the wizard
  // page's extractSchedulerError so both parsers agree).
  const unavailableMessage = schedulerUnavailableMessage(err)
  if (unavailableMessage !== null) {
    const e = new Error(unavailableMessage) as Error & {
      error?: string
      action?: string
      status?: number
    }
    e.error = 'scheduler_unavailable'
    e.action = 'retry'
    e.status = 503
    return e
  }
  // Everything else — including every structured scheduler 422 that is not one
  // of the two slugs above (UNKNOWN_SLOT_DIVISION, CAPACITY_EXCEEDED, …). Keep
  // the backend's message and code; the page routes its fix-config affordance
  // off that code.
  const body = (err ?? {}) as { message?: unknown; status?: unknown }
  const message =
    typeof body.message === 'string' && body.message.trim() !== ''
      ? body.message
      : 'Operation failed'

  const normalized = (
    err instanceof Error ? err : new Error(message)
  ) as Error & {
    error?: string
    error_code?: string
    action?: string
    status?: number
  }

  const code = extractSchedulerErrorCode(err)
  if (code) {
    normalized.error_code = code
  }
  if (normalized.status === undefined && typeof body.status === 'number') {
    normalized.status = body.status
  }

  return normalized
}

/**
 * Result of a mutating scheduler action (retry / reset). Carries the normalized
 * error alongside the ok flag so the caller can surface a friendly message
 * WITHOUT reading the hook's `error` state — a synchronous post-`await` read of
 * that state can't see the just-set value (React stale closure). The hook never
 * throws for these (a deliberate prior contract), so callers discriminate on
 * `ok` and read `error` for display.
 */
export interface SchedulerMutationResult {
  ok: boolean
  error?: Error & { error?: string; action?: string; status?: number }
}

/**
 * Scheduler flight game from API
 */
export interface SchedulerFlightGame {
  id: string
  scheduler_flight_id: string
  schedule_id: string
  division_uid: string
  home_team: { id: string; name: string } | null
  visiting_team: { id: string; name: string } | null
  venue: { id: string; name: string; timezone: string | null } | null
  facility: { id: string; name: string } | null
  starts_at: string
  ends_at: string
  /** Per-division sequence (restarts at 1 per division — not flight-unique). */
  game_number: number
  /** Flight-unique number shown as "GAME #"; null until assigned at generation. */
  global_game_number: number | null
  approval_status: 'pending' | 'approved' | 'rejected'
  rejection_reason: string | null
  reviewed_by: { id: string; name: string } | null
  reviewed_at: string | null
  can_review: boolean
  is_applied: boolean
  game_id: string | null
  created_at: string
  updated_at: string
}

/**
 * Scheduler constraint violation from API
 */
export interface SchedulerViolation {
  id: string
  constraint_type: {
    id: string
    code: string
    name: string
    category: string
  } | null
  severity: 'error' | 'warning' | 'info'
  message: string
  resolution_suggestion: string | null
  context: Record<string, unknown> | null
  affected_game_numbers: number[] | null
  affected_team_uids: string[] | null
  is_prevented: boolean
  original_slot: Record<string, unknown> | null
  resolved_slot: Record<string, unknown> | null
}

/**
 * Violation detail from the solver diagnostic output.
 * Extracted from the job's response_payload by the API.
 */
export interface DivisionJobViolation {
  message: string
  resolution_suggestion: string | null
  constraint_type: string | null
  constraint_name: string | null
  severity: string
}

/**
 * Per-division job status from the scheduler API.
 * Each division being scheduled gets its own job entry.
 */
export interface DivisionJob {
  id: string
  division_id: string
  division_name: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  satisfaction_score: number | null
  games_count: number
  error_code: string | null
  error_message: string | null
  // Fail-soft (SN-7897 gate #5): on SOLVER_TIMEOUT the backend returns a
  // reduced per-team target the wizard offers via "Apply M and Retry".
  recommended_games_per_team: number | null
  // Strict-first: true IFF this division's solve fell back from the all-hard
  // strict attempt to the original soft model (inline per-game violations may
  // appear). false = strict won / skipped; null = pre-feature / unknown.
  constraints_relaxed: boolean | null
  // Fill contract (the fill-completeness audit). For a fill_feasible_slots
  // division the backend reports whether it left any legally-fillable slot
  // unused. As of 2026-06-30 these are INFORMATIONAL fill-maximality signals
  // surfaced as a note — they do NOT affect the score or the EXCELLENT label,
  // which report strictly on constraint violations. Null = a NON-fill division.
  // api2 lifts a KNOWN fill division's missing verdict (a pre-feature /
  // deploy-skew row) to `unknown` before it reaches the client, so the note is
  // never silently dropped for a fill division.
  allocated_slot_count?: number | null
  used_slot_count?: number | null
  unused_fillable_slot_count?: number | null
  fill_completeness_status?: FillCompletenessStatus | null
  fill_limited_reason?: string | null
  violations: DivisionJobViolation[]
  started_at: string | null
  completed_at: string | null
}

/**
 * The fill-completeness verdict for a `fill_feasible_slots` division. All four
 * values are INFORMATIONAL (surfaced as a note); none affects the score or the
 * EXCELLENT label (2026-06-30 — the label reports strictly on violations):
 *   - `complete`              — no legal unused capacity
 *   - `avoidable_underfill`   — ≥1 legal additional game fits the unused slots
 *   - `fill_limited_by_setup` — the setup (constraints/venues) caps the fill
 *   - `unknown`               — fill maximality could not be verified
 */
export type FillCompletenessStatus =
  | 'complete'
  | 'avoidable_underfill'
  | 'fill_limited_by_setup'
  | 'unknown'

/**
 * Capabilities that the API reports for the current flight state.
 * Drives which action buttons the UI renders.
 */
export interface SchedulerFlightCapabilities {
  can_retry: boolean
  can_reset: boolean
  can_cancel: boolean
  can_approve: boolean
  can_apply: boolean
  can_publish: boolean
  can_checkpoint: boolean
}

/**
 * One offending division in a preflight capacity/feasibility rejection.
 * `blocked_by` is present only on new scheduler images (Phase 4 diagnostic).
 */
export interface GenerationErrorDivision {
  division_uid?: string
  division_name?: string
  games_needed?: number
  achievable_games?: number
  max_games_per_team?: number
  blocked_by?: Array<Record<string, unknown>>
}

/**
 * Structured generation/preflight failure persisted by api2 from the scheduler
 * 422 detail. The `outcome`/`proof_level`/`blocked_by` fields are the
 * constraint-outcome diagnostic — additive, absent on older images.
 */
export interface GenerationError {
  code?: string
  message?: string
  divisions?: GenerationErrorDivision[]
  outcome?: string | null
  proof_level?: string | null
  blocked_by?: Array<Record<string, unknown>>
}

/**
 * Extended flight data returned when polling generation state.
 * Includes games, violations, and generation metadata.
 */
export interface SchedulerFlightGenerationState {
  id: string
  label: string
  display_label: string
  start_date: string | null
  end_date: string | null
  status:
    | 'draft'
    | 'pending'
    | 'processing'
    | 'generated'
    | 'partially_generated'
    | 'failed'
    | 'applied'
    | 'published'
    | 'cancelled'
  is_default: boolean
  sort_order: number
  // Generation fields (merged from SchedulerRequest):
  generation_status: string | null
  total_games_count: number
  approved_games_count: number
  rejected_games_count: number
  pending_games_count: number | null
  satisfaction_score: number | null
  // Fill contract flight rollup: the WORST child fill-completeness status
  // (avoidable_underfill > unknown > fill_limited_by_setup > complete) and the
  // total avoidable unused slots across children. Drives the flight-level fill
  // note (informational only — as of 2026-06-30 it no longer caps the score or
  // "Excellent" label). Null only when no child carries a known status (non-fill
  // / unclassified children — a known fill child's pre-feature null is lifted to
  // `unknown` by api2 so the note still surfaces).
  fill_completeness_status?: FillCompletenessStatus | null
  unused_fillable_slot_count?: number | null
  // The worst limiting child's reason, for "Limited by setup: <reason>" in the
  // summary (null for avoidable underfill — the client shows the unused count).
  fill_limited_reason?: string | null
  progress: number | null
  error: string | { code: string; message: string; details: unknown } | null
  /**
   * Structured preflight/generation failure (api2 persists the scheduler 422
   * detail here). Carries the error code, the per-division achievable maxes,
   * and — on new scheduler images — the constraint-outcome diagnostic
   * (`outcome`/`proof_level`/`blocked_by`) that frames the failure as
   * "Setup impossible" with the blocking hard rules. All optional / null-safe.
   */
  generation_error?: GenerationError | null
  approved_division_uids?: string[]
  locked_division_uids?: string[] // backward compat
  division_dispatch_order?: string[]
  // Absolute clock cadence (minutes from midnight) generated game starts snap
  // UP to: 0/null = unset (legacy off-grid timing), 30 = half-hour, 60 = hourly.
  game_start_cadence_minutes?: number | null
  has_checkpoint?: boolean
  checkpoint_saved_at?: string | null
  checkpoint_game_count?: number | null
  checkpoint_saved_by?: 'auto' | 'user' | null
  has_changes_since_checkpoint?: boolean
  version_number: number | null
  can_retry: boolean
  can_apply: boolean
  can_cancel: boolean
  division_jobs?: DivisionJob[]
  capabilities?: SchedulerFlightCapabilities
  violations_count?: number
  violations?: SchedulerViolation[]
  games?: SchedulerFlightGame[]
  submitted_at: string | null
  generated_at: string | null
  applied_at: string | null
  created_at: string
  updated_at: string
}

/** Distinguishes the two timeout reasons for the UI */
export type TimeoutReason = 'total' | 'stuck'

const POLL_INTERVAL_MS = 3000

/**
 * Compute the hard total-ceiling timeout based on the number of divisions.
 * Formula from arch doc: 5 min base + 30s per division
 */
function computeTotalTimeoutMs(divisionCount: number): number {
  return 5 * 60_000 + divisionCount * 30_000
}

/** Stuck timer: if progress doesn't change for 4 minutes */
const STUCK_TIMEOUT_MS = 4 * 60_000

/**
 * Derive a progress fingerprint from division_jobs so we can detect
 * when *any* status changes (pending->processing, processing->completed, etc.).
 */
function deriveProgressFingerprint(
  divisionJobs: DivisionJob[] | undefined
): string {
  if (!divisionJobs || divisionJobs.length === 0) {
    return ''
  }
  return divisionJobs.map((j) => `${j.id}:${j.status}`).join('|')
}

/**
 * Hook result interface
 */
interface UseSchedulerGenerationResult {
  flight: SchedulerFlightGenerationState | null
  loading: boolean
  error: Error | null
  isPolling: boolean
  isTimedOut: boolean
  timeoutReason: TimeoutReason | null
  isStuck: boolean
  fetchFlight: (flightId: string) => Promise<void>
  resetDivisions: (
    flightId: string,
    divisionUids: string[]
  ) => Promise<SchedulerMutationResult>
  cancelGeneration: (flightId: string) => Promise<boolean>
  retryGeneration: (flightId: string) => Promise<SchedulerMutationResult>
  retryFailedDivisions: (flightId: string) => Promise<SchedulerMutationResult>
  clearFlight: () => void
  startPolling: (flightId: string, divisionCount?: number) => void
  stopPolling: () => void
  keepWaiting: () => void
  updateDivisionApprovalStatus: (
    divisionUids: string[],
    approvalStatus: SchedulerFlightGame['approval_status']
  ) => void
  updateAllGamesApproved: () => void
}

/**
 * Custom hook for managing scheduler generation lifecycle.
 * Polls the flight endpoint for generation status updates,
 * supports two-timer timeout (total ceiling + stuck),
 * cancel, retry, and per-division progress tracking.
 *
 * Replaces the old useSchedulerRequest hook — the flight
 * object now carries generation state directly.
 */
export const useSchedulerGeneration = (
  initialFlightId?: string | null
): UseSchedulerGenerationResult => {
  const [flight, setFlight] = useState<SchedulerFlightGenerationState | null>(
    null
  )
  const [flightId, setFlightId] = useState<string | null>(
    initialFlightId || null
  )

  // Sync flightId state when prop changes (e.g., flight switch navigation)
  useEffect(() => {
    if (initialFlightId !== undefined) {
      setFlightId(initialFlightId || null)
    }
    if (initialFlightId === undefined || initialFlightId === null) {
      setFlightId(null)
      setFlight(null)
    }
  }, [initialFlightId])

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [isPolling, setIsPolling] = useState<boolean>(false)
  const [isTimedOut, setIsTimedOut] = useState<boolean>(false)
  const [timeoutReason, setTimeoutReason] = useState<TimeoutReason | null>(null)
  const [isStuck, setIsStuck] = useState<boolean>(false)

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollStartTimeRef = useRef<number | null>(null)
  const lastProgressFingerprintRef = useRef<string>('')
  const lastProgressTimeRef = useRef<number>(Date.now())
  const totalTimeoutMsRef = useRef<number>(computeTotalTimeoutMs(1))
  const pollingFlightIdRef = useRef<string | null>(null)

  const clearPollInterval = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  /**
   * Fetch a flight's generation state by ID
   */
  const fetchFlight = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)

    try {
      const { data } = await req(`/scheduler/flights/${id}`)
      const flightData = data?.data ?? data
      setFlight(flightData)
      setFlightId(id)
      return flightData
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching the flight')
      )
      setFlight(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  // Auto-fetch flight data when flightId changes (e.g., page refresh / flight switch)
  // so generation_status is available for the review tab.
  useEffect(() => {
    if (flightId && !pollIntervalRef.current) {
      fetchFlight(flightId)
    }
  }, [flightId, fetchFlight])

  /**
   * Poll for flight generation status updates with two-timer timeout logic.
   */
  const pollFlight = useCallback(
    async (uid: string) => {
      try {
        const { data } = await req(`/scheduler/flights/${uid}`)
        const flightData = data?.data ?? data
        setFlight(flightData)

        const status = flightData?.generation_status

        // Terminal statuses: stop polling
        if (
          status === 'generated' ||
          status === 'draft' ||
          status === 'partially_generated' ||
          status === 'failed' ||
          status === 'cancelled' ||
          status === 'applied' ||
          status === 'published'
        ) {
          clearPollInterval()
          setIsPolling(false)
          return
        }

        // --- Two-timer timeout logic ---
        const now = Date.now()

        // 1) Check total ceiling timer
        if (
          pollStartTimeRef.current &&
          now - pollStartTimeRef.current > totalTimeoutMsRef.current
        ) {
          clearPollInterval()
          setIsPolling(false)
          setIsTimedOut(true)
          setTimeoutReason('total')
          return
        }

        // 2) Track progress changes via division_jobs fingerprint
        const fingerprint = deriveProgressFingerprint(flightData?.division_jobs)
        if (fingerprint !== lastProgressFingerprintRef.current) {
          // Progress changed — reset stuck timer
          lastProgressFingerprintRef.current = fingerprint
          lastProgressTimeRef.current = now
          setIsStuck(false)
        } else if (now - lastProgressTimeRef.current > STUCK_TIMEOUT_MS) {
          // No progress for 4 minutes — show stuck UI
          clearPollInterval()
          setIsPolling(false)
          setIsStuck(true)
          setIsTimedOut(true)
          setTimeoutReason('stuck')
          return
        }
      } catch {
        // On network error, keep polling (don't stop)
      }
    },
    [clearPollInterval]
  )

  /**
   * Start polling for flight generation status.
   * @param fId - the flight ID
   * @param divisionCount - number of divisions being scheduled (for dynamic timeout)
   */
  const startPolling = useCallback(
    (fId: string, divisionCount?: number) => {
      clearPollInterval()
      setIsPolling(true)
      setIsTimedOut(false)
      setTimeoutReason(null)
      setIsStuck(false)
      setFlightId(fId)
      pollingFlightIdRef.current = fId
      pollStartTimeRef.current = Date.now()
      lastProgressTimeRef.current = Date.now()
      lastProgressFingerprintRef.current = ''
      totalTimeoutMsRef.current = computeTotalTimeoutMs(divisionCount ?? 1)

      // Initial fetch immediately
      pollFlight(fId)

      // Then poll at interval
      pollIntervalRef.current = setInterval(() => {
        pollFlight(fId)
      }, POLL_INTERVAL_MS)
    },
    [clearPollInterval, pollFlight]
  )

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    clearPollInterval()
    setIsPolling(false)
    pollStartTimeRef.current = null
    pollingFlightIdRef.current = null
  }, [clearPollInterval])

  /**
   * Dismiss timeout / stuck state and resume polling.
   * Called when the user clicks "Keep Waiting".
   */
  const keepWaiting = useCallback(() => {
    const currentFlightId = pollingFlightIdRef.current || flightId
    if (!currentFlightId) {
      return
    }
    setIsTimedOut(false)
    setTimeoutReason(null)
    setIsStuck(false)
    // Reset the stuck timer baseline
    lastProgressTimeRef.current = Date.now()
    // Resume polling (keep the total timer running from original start)
    setIsPolling(true)
    pollFlight(currentFlightId)
    pollIntervalRef.current = setInterval(() => {
      pollFlight(currentFlightId)
    }, POLL_INTERVAL_MS)
  }, [flightId, pollFlight])

  /**
   * Reset specific divisions back to pending by removing their generated games
   */
  const resetDivisions = useCallback(
    async (
      id: string,
      divisionUids: string[]
    ): Promise<SchedulerMutationResult> => {
      try {
        const { data } = await req(`/scheduler/flights/${id}/reset-divisions`, {
          method: 'POST',
          body: JSON.stringify({ division_uids: divisionUids }),
        })
        const flightData = data?.data ?? data
        setFlight(flightData)
        return { ok: true }
      } catch (err) {
        const normalized = toSchedulerError(err)
        setError(normalized)
        return { ok: false, error: normalized }
      }
    },
    []
  )

  /**
   * Cancel a flight's generation that is currently processing
   */
  const cancelGeneration = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { data } = await req(`/scheduler/flights/${id}/cancel`, {
          method: 'POST',
        })
        const flightData = data?.data ?? data
        setFlight(flightData)
        clearPollInterval()
        setIsPolling(false)
        return true
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Operation failed'))
        return false
      }
    },
    [clearPollInterval]
  )

  /**
   * Retry a failed/cancelled flight generation (all divisions)
   */
  const retryGeneration = useCallback(
    async (id: string): Promise<SchedulerMutationResult> => {
      try {
        const { data } = await req(`/scheduler/flights/${id}/retry`, {
          method: 'POST',
        })
        const flightData = data?.data ?? data
        setFlight(flightData)
        return { ok: true }
      } catch (err) {
        const normalized = toSchedulerError(err)
        setError(normalized)
        return { ok: false, error: normalized }
      }
    },
    []
  )

  /**
   * Retry only the failed divisions of a partially_generated flight
   */
  const retryFailedDivisions = useCallback(
    async (id: string): Promise<SchedulerMutationResult> => {
      try {
        const { data } = await req(`/scheduler/flights/${id}/retry`, {
          method: 'POST',
          body: JSON.stringify({ failed_only: true }),
        })
        const flightData = data?.data ?? data
        setFlight(flightData)
        return { ok: true }
      } catch (err) {
        const normalized = toSchedulerError(err)
        setError(normalized)
        return { ok: false, error: normalized }
      }
    },
    []
  )

  /**
   * Update all games in specific divisions + recompute approved_division_uids (no refetch)
   */
  const updateDivisionApprovalStatus = useCallback(
    (
      divisionUids: string[],
      approvalStatus: SchedulerFlightGame['approval_status']
    ) => {
      setFlight((prev) => {
        if (!prev?.games) {
          return prev
        }
        const divSet = new Set(divisionUids)
        const updatedGames = prev.games.map((g) =>
          divSet.has(g.division_uid)
            ? { ...g, approval_status: approvalStatus }
            : g
        )
        const approvedDivUids = [
          ...new Set(updatedGames.map((g) => g.division_uid)),
        ].filter((uid) =>
          updatedGames
            .filter((g) => g.division_uid === uid)
            .every((g) => g.approval_status === 'approved')
        )
        return {
          ...prev,
          games: updatedGames,
          approved_division_uids: approvedDivUids,
        }
      })
    },
    []
  )

  /**
   * Mark ALL games as approved + update approved_division_uids (no refetch)
   */
  const updateAllGamesApproved = useCallback(() => {
    setFlight((prev) => {
      if (!prev?.games) {
        return prev
      }
      const updatedGames = prev.games.map((g) => ({
        ...g,
        approval_status: 'approved' as const,
      }))
      const allDivUids = [...new Set(updatedGames.map((g) => g.division_uid))]
      return {
        ...prev,
        games: updatedGames,
        approved_division_uids: allDivUids,
      }
    })
  }, [])

  /**
   * Clear the current flight generation state
   */
  const clearFlight = useCallback(() => {
    stopPolling()
    setFlight(null)
    setFlightId(null)
    setError(null)
    setIsTimedOut(false)
    setTimeoutReason(null)
    setIsStuck(false)
  }, [stopPolling])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPollInterval()
    }
  }, [clearPollInterval])

  return {
    flight,
    loading,
    error,
    isPolling,
    isTimedOut,
    timeoutReason,
    isStuck,
    fetchFlight,
    resetDivisions,
    cancelGeneration,
    retryGeneration,
    retryFailedDivisions,
    clearFlight,
    startPolling,
    stopPolling,
    keepWaiting,
    updateDivisionApprovalStatus,
    updateAllGamesApproved,
  }
}

/** @deprecated Use SchedulerFlightGame instead */
export type { SchedulerFlightGame as SchedulerRequestGame }

export default useSchedulerGeneration
